import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Menu, X, User, LogOut, LayoutDashboard, Shield, MessageSquare, HelpCircle, ChevronDown, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import CookieConsent from './CookieConsent';
import NewsletterSection from './NewsletterSection';
import AnnouncementBanner from './AnnouncementBanner';
import PromoBanner from './PromoBanner';
import ExitIntentPopup from './ExitIntentPopup';

import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import { FooterAd } from './AdBanner';
import MobileBottomNav from './MobileBottomNav';
import axios from 'axios';
import { MessagesProvider, useMessages } from '../contexts/MessagesContext';

const API = '/api';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Auto-close mobile menu on route change + focus management for accessibility
  useEffect(() => {
    setMobileMenuOpen(false);
    // Move focus to main content on route change for screen readers
    const main = document.getElementById('main-content');
    if (main) {
      main.focus({ preventScroll: true });
    }
  }, [location.pathname]);

  const { unreadCount, showMessages: showMessages_ } = useMessages();
  const [notifCount, setNotifCount] = useState(0);
  // showMessages drives the nav link visibility
  const showMessages = showMessages_ || unreadCount > 0;
  
  // Don't show newsletter on homepage (it renders its own) or admin pages
  const isHomePage = location.pathname === '/';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isJoinPage = location.pathname === '/join';
  const isGetListedLP = location.pathname === '/get-listed';
  const showNewsletter = !isHomePage && !isAdminPage && !isJoinPage && !isGetListedLP;

  // Check if user should see Messages and get unread count
  useEffect(() => {
    if (user) {
      fetchNotifCount();
    } else {
      setNotifCount(0);
    }
  }, [user]);

  // Periodically refresh notification count for mobile badge
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchNotifCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchNotifCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setNotifCount(res.data.unread_count || 0);
    } catch { setNotifCount(0); }
  };

  // Landing pages render their own chrome — bypass Layout entirely
  if (isJoinPage || isGetListedLP) return <>{children}</>;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const confirmLogout = () => setLogoutDialogOpen(true);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to content — accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[10000] focus:bg-[#0F2C3E] focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-semibold focus:shadow-lg" data-testid="skip-to-content">
        Skip to main content
      </a>
      <AnnouncementBanner />
      <PromoBanner variant="strip" surface="site_header_strip" />
      <ExitIntentPopup />
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 dark:bg-slate-900/90 border-b border-slate-100 dark:border-slate-800">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="home-link">
              {/* Custom Logo Mark */}
              <div className="relative w-9 h-9">
                {/* Background circle with gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F2C3E] to-[#1a4a5e] dark:from-white/20 dark:to-white/5 rounded-xl rotate-3 group-hover:rotate-6 transition-transform duration-300" />
                {/* Inner design - stylized 'E' for Essex */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 6h12M4 12h8M4 18h12" />
                    <circle cx="18" cy="12" r="2" fill="currentColor" stroke="none" />
                  </svg>
                </div>
                {/* Accent dot */}
                <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#D4A017] rounded-full border-2 border-white dark:border-slate-900" />
              </div>
              {/* Text */}
              <div className="flex flex-col -space-y-1">
                <span className="text-lg font-bold text-[#0F2C3E] dark:text-white tracking-tight">Discover</span>
                <span className="text-sm font-semibold text-[#D4A017] tracking-wide">ESSEX</span>
              </div>
            </Link>

            <nav className="hidden xl:flex items-center gap-6">
              <Link to="/businesses" className="text-slate-700 dark:text-slate-300 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-businesses">
                Businesses
              </Link>
              <Link to="/jobs" className="text-slate-700 dark:text-slate-300 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-jobs">
                Jobs
              </Link>
              <Link to="/events" className="text-slate-700 dark:text-slate-300 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-events">
                Events
              </Link>
              <Link to="/marketplace" className="text-slate-700 dark:text-slate-300 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-classified">
                Marketplace
              </Link>
              <Link to="/find-a-trade" className="text-slate-700 dark:text-slate-300 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-requests">
                Find a Trade
              </Link>
              <span className="text-slate-300 dark:text-slate-600 mx-1 select-none">|</span>
              <div className="relative group" data-testid="nav-community-dropdown">
                <div className="flex items-center gap-1">
                  <Link
                    to="/community"
                    className="flex items-center gap-1 text-slate-700 dark:text-slate-300 hover:text-[#0F2C3E] dark:hover:text-white transition-colors"
                    data-testid="nav-community"
                  >
                    Community
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#0F2C3E] dark:group-hover:text-white transition-transform group-hover:rotate-180" />
                  </Link>
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-100 dark:border-slate-700 py-1.5 min-w-[180px]">
                    <Link to="/community" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-dropdown-community-hub">
                      Community Hub
                    </Link>
                    <Link to="/best-of-essex" className="flex items-center justify-between px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-dropdown-bestof">
                      Best of Essex
                      <span className="bg-red-500 text-white text-[9px] font-bold rounded px-1 py-0.5">Vote Now</span>
                    </Link>
                    <Link to="/pricing" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-dropdown-pricing">
                      Pricing
                    </Link>
                    <Link to="/why-join" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-dropdown-join">
                      Why Join?
                    </Link>
                    <Link to="/towns" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-dropdown-towns">
                      Essex Towns
                    </Link>
                    <Link to="/blog" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-[#0F2C3E] dark:hover:text-white transition-colors" data-testid="nav-dropdown-blog">
                      Our Blog
                    </Link>
                  </div>
                </div>
              </div>
              {user && <NotificationBell />}
              {!user && (
              <Link
                to="/pricing"
                className="text-slate-700 dark:text-slate-300 hover:text-[#0F2C3E] dark:hover:text-white text-sm transition-colors whitespace-nowrap"
                data-testid="nav-pricing"
              >
                Pricing
              </Link>
              )}
              {user ? (
                <>
                  {showMessages && (
                    <Link to="/messages" data-testid="nav-messages" className="relative">
                      <Button variant="ghost" size="sm" className="gap-2">
                        <div className="relative">
                          <MessageSquare className="h-4 w-4" />
                          {unreadCount > 0 && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </div>
                        Messages
                      </Button>
                    </Link>
                  )}
                  <Link to="/dashboard" data-testid="nav-dashboard">
                    <Button variant="ghost" size="sm" className="gap-2">
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Button>
                  </Link>
                  {user.show_upgrade_cta && (
                    <Link to="/pricing" data-testid="header-upgrade-btn">
                      <Button size="sm" className="bg-[#D4A017] hover:bg-[#F5B728] text-white font-bold gap-1 shadow-sm">
                        Upgrade →
                      </Button>
                    </Link>
                  )}
                  {(user.role === 'admin' || user.role === 'moderator') && (
                    <Link to="/admin" data-testid="nav-admin">
                      <Button variant="ghost" size="sm" className="gap-2 text-[#D4A017]">
                        <Shield className="h-4 w-4" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Button onClick={confirmLogout} variant="ghost" size="sm" className="gap-2" data-testid="logout-btn">
                    <LogOut className="h-4 w-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" data-testid="nav-login">
                    <Button variant="ghost" size="sm">Login</Button>
                  </Link>
                  <Link to="/signup" data-testid="nav-signup">
                    <Button className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 dark:bg-[#D4A017] dark:text-[#0F2C3E] dark:hover:bg-[#F5B728]" size="sm">Join Free</Button>
                  </Link>
                </>
              )}
            </nav>

            <div className="flex items-center gap-1 xl:hidden">
              <button
                className="relative"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
              {mobileMenuOpen ? <X className="h-6 w-6 dark:text-slate-200" /> : <Menu className="h-6 w-6 dark:text-slate-200" />}
              {!mobileMenuOpen && (unreadCount + notifCount) > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5" data-testid="mobile-menu-badge">
                  {(unreadCount + notifCount) > 99 ? '99+' : (unreadCount + notifCount)}
                </span>
              )}
            </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="xl:hidden py-4 border-t border-slate-100 dark:border-slate-700" data-testid="mobile-menu">
              <nav className="flex flex-col gap-4">
                <Link to="/businesses" className="text-slate-700 dark:text-slate-200" onClick={() => setMobileMenuOpen(false)}>
                  Businesses
                </Link>
                <Link to="/jobs" className="text-slate-700 dark:text-slate-200" onClick={() => setMobileMenuOpen(false)}>
                  Jobs
                </Link>
                <Link to="/events" className="text-slate-700 dark:text-slate-200" onClick={() => setMobileMenuOpen(false)}>
                  Events
                </Link>
                <Link to="/marketplace" className="text-slate-700 dark:text-slate-200" onClick={() => setMobileMenuOpen(false)}>
                  Marketplace
                </Link>
                <Link to="/find-a-trade" className="relative inline-flex items-center text-slate-700 dark:text-slate-200" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-requests">
                  Find a Trade
                </Link>
                <Link to="/community" className="text-slate-700 dark:text-slate-200 font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-community">
                  Community
                </Link>
                <div className="pl-4 flex flex-col gap-3 border-l-2 border-slate-100 dark:border-slate-700">
                  <Link to="/community" className="text-sm text-slate-500 dark:text-slate-400" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-community-hub">
                    Community Hub
                  </Link>
                  <Link to="/best-of-essex" className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5" onClick={() => setMobileMenuOpen(false)}>
                    Best of Essex <span className="ml-2 bg-red-500 text-white text-[9px] font-bold rounded px-1 py-0.5">Vote Now</span>
                  </Link>
                  <Link to="/pricing" className="text-sm text-slate-500 dark:text-slate-400" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-dropdown-pricing">
                    Pricing
                  </Link>
                  <Link to="/towns" className="text-sm text-slate-500 dark:text-slate-400" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-towns">
                    Essex Towns
                  </Link>
                  <Link to="/blog" className="text-sm text-slate-500 dark:text-slate-400" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-blog">
                    Our Blog
                  </Link>
                </div>
                {!user && (
                <Link to="/pricing" className="text-slate-700 dark:text-slate-200" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-pricing">
                  Pricing
                </Link>
                )}
                {user ? (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 rounded-lg" onClick={(e) => { const bell = e.currentTarget.querySelector('[data-testid="notification-bell"]'); if (bell) bell.click(); }}>
                      <NotificationBell />
                      <span className="text-sm text-slate-700">Notifications</span>
                    </div>
                    {showMessages && (
                      <Link to="/messages" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start gap-2">
                          <div className="relative">
                            <MessageSquare className="h-4 w-4" />
                            {unreadCount > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                          </div>
                          Messages
                        </Button>
                      </Link>
                    )}
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start gap-2">
                        <LayoutDashboard className="h-4 w-4" />
                        Dashboard
                      </Button>
                    </Link>
                    {user.show_upgrade_cta && (
                      <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-upgrade-btn">
                        <Button className="w-full bg-[#D4A017] hover:bg-[#F5B728] text-white font-bold justify-start">
                          Upgrade →
                        </Button>
                      </Link>
                    )}
                    {(user.role === 'admin' || user.role === 'moderator') && (
                      <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start gap-2 text-[#D4A017]">
                          <Shield className="h-4 w-4" />
                          Admin
                        </Button>
                      </Link>
                    )}
                    <Button onClick={() => { setMobileMenuOpen(false); confirmLogout(); }} variant="ghost" className="justify-start gap-2">
                      <LogOut className="h-4 w-4" />
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">Login</Button>
                    </Link>
                    <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="bg-[#0F2C3E] dark:bg-[#D4A017] dark:text-[#0F2C3E] dark:hover:bg-[#F5B728] w-full">Join Free</Button>
                    </Link>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="flex-1 outline-none">{children}</main>

      {/* Footer Ad Banner - above newsletter, skip on homepage (renders its own) */}
      {!isHomePage && (
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4">
          <FooterAd />
        </div>
      )}

      {/* Newsletter Section - show on all pages except homepage and admin */}
      {showNewsletter && <NewsletterSection />}

      {/* Cookie Consent Banner */}
      <CookieConsent />

      <footer className="bg-[#0F2C3E] text-white py-12" role="contentinfo">
        {/* Subtle separator between newsletter and footer content */}
        <div className="container mx-auto px-4 md:px-6 lg:px-8 -mt-12 mb-8">
          <div className="border-t border-white/10" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">

          {/* Popular Towns — SEO-rich internal links */}
          <nav className="mb-10 pb-8 border-b border-white/10" aria-label="Essex towns">
            <div className="flex flex-col items-center mb-4 gap-1">
              <h4 className="font-semibold">Popular Essex Towns</h4>
              <Link to="/towns" className="text-sm text-[#D4A017] hover:text-[#D4A017]/80 transition-colors">
                View all 32 towns &rarr;
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {['Chelmsford', 'Colchester', 'Southend-on-Sea', 'Basildon', 'Brentwood', 'Harlow', 'Romford', 'Clacton-on-Sea', 'Maldon', 'Leigh-on-Sea'].map((town) => (
                <Link
                  key={town}
                  to={`/town/${town.toLowerCase().replace(/ /g, '-')}`}
                  className="text-sm text-slate-300 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-full transition-colors"
                >
                  {town}
                </Link>
              ))}
            </div>
          </nav>

          {/* Main Footer Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-10 pb-8 border-b border-white/10">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="relative w-8 h-8">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-white/5 rounded-xl rotate-3" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 6h12M4 12h8M4 18h12" />
                      <circle cx="18" cy="12" r="2" fill="currentColor" stroke="none" />
                    </svg>
                  </div>
                  <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#D4A017] rounded-full" />
                </div>
                <div className="flex flex-col -space-y-1">
                  <span className="text-base font-bold text-white tracking-tight">Discover</span>
                  <span className="text-xs font-semibold text-[#D4A017] tracking-wide">ESSEX</span>
                </div>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">
                Essex's free local directory. Find businesses, jobs, events, marketplace listings and community services across the county.
              </p>
            </div>

            {/* Directory */}
            <nav aria-label="Directory links">
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-white/80">Directory</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/business" className="hover:text-white transition-colors">Business</Link></li>
                <li><Link to="/jobs" className="hover:text-white transition-colors">Jobs</Link></li>
                <li><Link to="/events" className="hover:text-white transition-colors">Events</Link></li>
                <li><Link to="/marketplace" className="hover:text-white transition-colors">Marketplace</Link></li>
                <li><Link to="/find-a-trade" className="hover:text-white transition-colors">Find a Trade</Link></li>
              </ul>
            </nav>

            {/* For Businesses */}
            <nav aria-label="Business links">
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-white/80">For Businesses</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/create-listing" className="hover:text-white transition-colors">Add Free Listing</Link></li>
                <li><Link to="/claim-business" className="hover:text-white transition-colors">Claim Your Business</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing &amp; Plans</Link></li>
                <li><Link to="/blog/discover-essex-now-welcomes-uk-wide-businesses" className="hover:text-white transition-colors">UK Businesses Welcome</Link></li>
                <li><Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link></li>
                <li><Link to="/help" className="hover:text-white transition-colors">Help &amp; Support</Link></li>
              </ul>
            </nav>

            {/* Advertise by Category */}
            <nav aria-label="Advertising links">
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-white/80">Advertise in Essex</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/advertise/plumbers" className="hover:text-white transition-colors">Advertising for Plumbers</Link></li>
                <li><Link to="/advertise/restaurants" className="hover:text-white transition-colors">Advertising for Restaurants</Link></li>
                <li><Link to="/advertise/solicitors" className="hover:text-white transition-colors">Advertising for Solicitors</Link></li>
                <li><Link to="/advertise/builders" className="hover:text-white transition-colors">Advertising for Builders</Link></li>
                <li><Link to="/advertise/tradesmen" className="hover:text-white transition-colors">Advertising for Tradesmen</Link></li>
                <li><Link to="/advertise/essex-businesses" className="hover:text-white transition-colors">All Business Categories</Link></li>
              </ul>
            </nav>

            {/* Community */}
            <nav aria-label="Community links">
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-white/80">Community</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/towns" className="hover:text-white transition-colors">Essex Towns</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Our Blog</Link></li>
                <li><Link to="/community" className="hover:text-white transition-colors">Community Posts</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/how-we-verify" className="hover:text-white transition-colors">How We Verify Listings</Link></li>
                <li><Link to="/how-we-rank" className="hover:text-white transition-colors">How We Rank Listings</Link></li>
                <li><a href="/refer" className="hover:text-white transition-colors">Earn Free Rewards</a></li>
              </ul>
            </nav>

            {/* Legal */}
            <nav aria-label="Legal links">
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wider text-white/80">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/advertise" className="hover:text-white transition-colors">Advertise With Us</Link></li>
              </ul>
            </nav>
          </div>
          
          {/* Bottom Bar — Social + Copyright */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Discover Essex. All rights reserved.</p>
            <div className="flex items-center gap-5">
              <span className="text-xs text-slate-500">Follow us</span>
              <a href="https://www.facebook.com/DiscoverEssexUK" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" aria-label="Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/DiscoverEssex" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" aria-label="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/discover-essex/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" aria-label="LinkedIn">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://share.google/9A0wOCJhgnRrCrYYd" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" aria-label="Google Business Profile" data-testid="google-business-link">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              </a>
              <span className="text-slate-600 mx-1">|</span>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </footer>
      <MobileBottomNav />

      {/* Logout confirmation dialog */}
      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Log out of Discover Essex?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll need to sign in again to access your dashboard and listings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogout}
              className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 text-white"
              data-testid="logout-confirm-btn"
            >
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default function LayoutWithMessages(props) {
  return (
    <MessagesProvider>
      <Layout {...props} />
    </MessagesProvider>
  );
}